import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { Trash } from 'react-feather';
import styled from '@emotion/styled';
import type { CategoryGroup } from '../types';

type ModalComponent = React.ComponentType<{
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, colors: string[], categoryId: number, paletteId?: number) => void;
  initialColors?: string[];
  initialName?: string;
  categories: CategoryGroup[];
  defaultCategoryId?: number;
  disableCategorySelection?: boolean;
  mode?: 'create' | 'edit';
  paletteId?: number;
  ModalComponent: ModalComponent;
};

const DEFAULT_MODAL_COLORS = [
  '#4D7FB3',
  '#DE674F',
  '#47C5C5',
  '#6784CC',
  '#B7D97C',
];

const MIN_COLORS = 5;
const MAX_COLORS = 25;

export const PaletteCreationModal = ({
  open,
  onClose,
  onSave,
  initialColors = DEFAULT_MODAL_COLORS,
  initialName = '',
  categories,
  defaultCategoryId,
  disableCategorySelection = false,
  mode = 'create',
  paletteId,
  ModalComponent
}: Props): React.JSX.Element => {
  const [paletteName, setPaletteName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(
    defaultCategoryId || categories[0]?.category.id || 1
  );
  const [colors, setColors] = useState<string[]>(initialColors);
  const [colorInputValues, setColorInputValues] = useState<Record<number, string>>({});

  useEffect(() => {
    if (open && defaultCategoryId) {
      setSelectedCategoryId(defaultCategoryId);
    }
  }, [open, defaultCategoryId]);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialName) {
        setPaletteName(initialName);
      }
      setColors(initialColors);
      setColorInputValues({});
    }
  }, [open, mode, initialName, initialColors]);

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPaletteName(e.target.value);
  }, []);

  const handleColorInputChange = useCallback((index: number, value: string) => {
    setColorInputValues(prev => ({ ...prev, [index]: value }));
  }, []);

  const validateAndUpdateColor = useCallback((index: number, value: string) => {
    const cleanValue = value.replace('#', '').toUpperCase();
    if (/^[0-9A-F]{6}$/i.test(cleanValue)) {
      const hexColor = `#${cleanValue}`;
      setColors(prev => {
        const updated = [...prev];
        updated[index] = hexColor;
        return updated;
      });
      setColorInputValues(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return hexColor;
    }
    return null;
  }, []);

  const handleColorBlur = useCallback((index: number) => {
    const inputValue = colorInputValues[index];
    if (inputValue !== undefined) {
      const validated = validateAndUpdateColor(index, inputValue);
      if (!validated) {
        setColorInputValues(prev => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
      }
    }
  }, [colorInputValues, validateAndUpdateColor]);

  const handleAddColor = useCallback(() => {
    if (colors.length < MAX_COLORS) {
      const lastColor = colors[colors.length - 1];
      if (lastColor) {
        setColors(prev => [...prev, lastColor]);
      }
    }
  }, [colors]);

  const handleRemoveColor = useCallback((index: number) => {
    if (colors.length > MIN_COLORS) {
      setColors(prev => prev.filter((_, i) => i !== index));
      setColorInputValues(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  }, [colors.length]);

  const handleCancel = useCallback(() => {
    setPaletteName('');
    setSelectedCategoryId(defaultCategoryId || categories[0]?.category.id || 1);
    setColors(initialColors);
    setColorInputValues({});
    onClose();
  }, [initialColors, defaultCategoryId, categories, onClose]);

  const handleSave = useCallback(() => {
    if (!paletteName.trim()) return;
    if (mode === 'edit' && paletteId) {
      onSave(paletteName.trim(), colors, selectedCategoryId, paletteId);
    } else {
      onSave(paletteName.trim(), colors, selectedCategoryId);
    }
    handleCancel();
  }, [handleCancel, paletteName, colors, selectedCategoryId, mode, paletteId, onSave]);

  const canSave = paletteName.trim().length > 0 && colors.length >= MIN_COLORS;
  const canRemove = colors.length > MIN_COLORS;

  const columnLength = Math.ceil(MAX_COLORS / 2);
  const leftColumn = colors.slice(0, columnLength);
  const rightColumn = colors.slice(columnLength);

  return (
    <ModalComponent open={open} onClose={handleCancel}>
      <ModalRoot>
        <ModalWrap>
          <ModalHeader>Edit Template</ModalHeader>

          <ModalMain>
            <NameInputContainer>
              <NameInput
                type="text"
                value={paletteName}
                onChange={handleNameChange}
                placeholder="Theme name"
                autoFocus
              />
            </NameInputContainer>

            <PaletteContainer>
              <PaletteScroll>
                <PaletteWrap>
                  <PaletteMain>
                    {/* Left Column */}
                    <PaletteColumn>
                      {leftColumn.map((color, i) => (
                        <ColorItem key={i}>
                          <ColorInputWrap>
                            <ColorSwatch style={{ backgroundColor: color }} />
                            <ColorInputMain>
                              <ColorInputPrefix>#</ColorInputPrefix>
                              <ColorInputField
                                type="text"
                                value={colorInputValues[i] ?? color.replace('#', '').toUpperCase()}
                                onChange={(e) => handleColorInputChange(i, e.target.value)}
                                onBlur={() => handleColorBlur(i)}
                                maxLength={6}
                              />
                            </ColorInputMain>
                          </ColorInputWrap>
                          {canRemove && (
                            <RemoveButton onClick={() => handleRemoveColor(i)}>
                              <Trash size={18} />
                            </RemoveButton>
                          )}
                        </ColorItem>
                      ))}
                    </PaletteColumn>

                    {/* Right Column */}
                    <PaletteColumn>
                      {rightColumn.map((color, i) => {
                        const actualIndex = i + columnLength;
                        return (
                          <ColorItem key={actualIndex}>
                            <ColorInputWrap>
                              <ColorSwatch style={{ backgroundColor: color }} />
                              <ColorInputMain>
                                <ColorInputPrefix>#</ColorInputPrefix>
                                <ColorInputField
                                  type="text"
                                  value={colorInputValues[actualIndex] ?? color.replace('#', '').toUpperCase()}
                                  onChange={(e) => handleColorInputChange(actualIndex, e.target.value)}
                                  onBlur={() => handleColorBlur(actualIndex)}
                                  maxLength={6}
                                />
                              </ColorInputMain>
                            </ColorInputWrap>
                            {canRemove && (
                              <RemoveButton onClick={() => handleRemoveColor(actualIndex)}>
                                <Trash size={18} />
                              </RemoveButton>
                            )}
                          </ColorItem>
                        );
                      })}
                    </PaletteColumn>
                  </PaletteMain>
                </PaletteWrap>
              </PaletteScroll>
            </PaletteContainer>

            {colors.length < MAX_COLORS && (
              <AddColorContainer>
                <AddColorButton onClick={handleAddColor}>
                  + Add Color
                </AddColorButton>
              </AddColorContainer>
            )}

            <ButtonSet>
              <CancelButton onClick={handleCancel}>
                Cancel
              </CancelButton>
              <SaveButton onClick={handleSave} disabled={!canSave}>
                Save
              </SaveButton>
            </ButtonSet>
          </ModalMain>
        </ModalWrap>
      </ModalRoot>
    </ModalComponent>
  );
};

PaletteCreationModal.displayName = 'PaletteCreationModal';

// Styled Components
const ModalRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  padding: '32px',
  boxSizing: 'border-box',
  width: '600px',
  maxWidth: '100vw',
});

const ModalWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minHeight: '100%',
  width: '100%',
});

const ModalMain = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '560px',
  minHeight: '500px',
  maxHeight: '80vh',
});

const ModalHeader = styled('h2')({
  margin: '0 0 32px 0',
  fontFamily: 'var(--font-bold)',
  fontSize: '22px',
  color: 'var(--black, #1a1a1a)',
  textAlign: 'center',
});

const NameInputContainer = styled('div')({
  marginBottom: '20px',
  height: '40px',
  flexShrink: 0,
});

const NameInput = styled('input')({
  width: '100%',
  height: '100%',
  padding: '10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  fontSize: '16px',
  fontFamily: 'var(--font-reg, inherit)',
  boxSizing: 'border-box',
  '&:focus': { outline: 'none', borderColor: 'var(--gray-m, #999)' },
  '&::placeholder': { color: 'var(--gray-l, #ccc)' },
});

const PaletteContainer = styled('div')({
  flex: 1,
  marginBottom: '20px',
  overflow: 'hidden',
  minHeight: '300px',
});

const PaletteScroll = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  overflowY: 'auto',
  height: '100%',
  paddingRight: '8px',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
});

const PaletteWrap = styled('div')({
  width: '100%',
});

const PaletteMain = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '20px',
  width: '100%',
});

const PaletteColumn = styled('div')({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const ColorItem = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  marginBottom: '10px',
  '&:hover > button': { display: 'block' },
});

const ColorInputWrap = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flex: 1,
});

const ColorSwatch = styled('div')({
  display: 'flex',
  height: '41px',
  width: '41px',
  borderRadius: '4px',
  flexShrink: 0,
});

const ColorInputMain = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '10px 5px 10px 15px',
  border: '1px solid var(--gray-l, #ddd)',
  borderRadius: '4px',
  boxSizing: 'border-box',
  cursor: 'text',
  flex: 1,
  '&:focus-within': { borderColor: 'var(--gray-m, #999)' },
});

const ColorInputPrefix = styled('span')({
  fontSize: '16px',
  color: '#333',
});

const ColorInputField = styled('input')({
  fontFamily: 'var(--font-reg, inherit)',
  border: 'none',
  fontSize: '16px',
  width: '4.5em',
  paddingRight: '5px',
  textTransform: 'uppercase',
  background: 'transparent',
  '&:focus': { outline: 'none' },
  '&::selection': { backgroundColor: 'var(--blue-l, #b3d9ff)' },
});

const RemoveButton = styled('button')({
  display: 'none',
  color: 'var(--gray-l, #ccc)',
  margin: '0 5px',
  transition: 'color 150ms ease-in-out',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  padding: '4px',
  '&:hover': { color: 'var(--gray, #999)' },
});

const AddColorContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '45px',
  marginBottom: '16px',
  flexShrink: 0,
});

const AddColorButton = styled('button')({
  minWidth: '200px',
  height: '100%',
  borderRadius: '6px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--font-bold)',
  fontSize: '16px',
  color: 'var(--black-l, #333)',
  transition: 'all 150ms ease-in-out',
  '&:hover': { backgroundColor: 'var(--gray-xl, #f5f5f5)', color: 'var(--black-l, #000)' },
  '&:active': { backgroundColor: 'var(--blue-xl, #e6f2ff)' },
});

const ButtonSet = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: '45px',
  gap: '12px',
  flexShrink: 0,
  marginTop: 'auto',
  paddingTop: '20px',
});

const CancelButton = styled('button')({
  width: '150px',
  height: '100%',
  fontFamily: "var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  fontSize: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '5px',
  border: '1px solid var(--gray, #999)',
  color: 'var(--gray-d, #666)',
  backgroundColor: 'var(--pri-01, white)',
  transition: 'background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in',
  '&:hover': { backgroundColor: 'var(--gray-xl, #f5f5f5)' },
  '&:disabled': {
    borderColor: 'var(--gray-l, #ddd)',
    color: 'var(--pri-01, white)',
    backgroundColor: 'var(--gray-l, #ddd)',
    cursor: 'not-allowed',
  },
});

const SaveButton = styled('button')({
  width: '150px',
  height: '100%',
  fontFamily: "var(--font-bold, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif)",
  fontSize: '16px',
  textAlign: 'center',
  cursor: 'pointer',
  borderRadius: '5px',
  border: '2px solid var(--pri-02, #4A90E2)',
  backgroundColor: 'var(--pri-02, #4A90E2)',
  color: 'var(--pri-01, white)',
  transition: 'background-color 180ms ease-in-out, border 120ms ease-in, border-color 120ms ease-in, color 120ms ease-in',
  '&:hover': { border: '2px solid var(--sec-02, #357ABD)', backgroundColor: 'var(--sec-02, #357ABD)' },
  '&:disabled': {
    borderColor: 'var(--gray-l, #ddd)',
    color: 'var(--pri-01, white)',
    backgroundColor: 'var(--gray-l, #ddd)',
    cursor: 'not-allowed',
  },
});
